import moment from 'moment'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import { addFlexibleMonthsIfNoneSelected } from 'lib/search/searchUtils'
import { isHotelFlexibleNights } from '../ListingDrawerSearchDates'
import { getValidHotelDateSelection } from 'tripPlanner/utils/resolveSearchDefaults'
import type {
  ListingDrawerHotelDateSelection,
  ListingDrawerHotelFlexibleNights,
} from '../ListingDrawerSearchDates'

export interface HotelSearchDateParams {
  checkIn?: string
  checkOut?: string
  flexibleMonths?: string
  flexibleNights?: ListingDrawerHotelFlexibleNights
}

export function getHotelSearchDateParams(
  selection: ListingDrawerHotelDateSelection,
): HotelSearchDateParams {
  if (selection.type === 'specific') {
    return {
      checkIn: selection.checkIn,
      checkOut: selection.checkOut,
    }
  }

  if (selection.type === 'flexible' && selection.flexibleMonths) {
    return {
      flexibleMonths: selection.flexibleMonths,
      flexibleNights: selection.flexibleNights,
    }
  }

  return {}
}

export function getDefaultHotelSearchDateSelection(
  checkIn?: string,
  checkOut?: string,
): ListingDrawerHotelDateSelection {
  const resolvedCheckOut = checkIn && checkOut === checkIn
    ? moment(checkIn).add(1, 'day').format('YYYY-MM-DD')
    : checkOut

  return checkIn && resolvedCheckOut
    ? { type: 'specific', checkIn, checkOut: resolvedCheckOut }
    : { type: 'anytime' }
}

export function getHotelSearchDateSelectionFromParams(
  params: OfferClickSearchParams,
): ListingDrawerHotelDateSelection {
  if (params.flexibleNights) {
    const nights = params.flexibleNights
    if (isHotelFlexibleNights(nights)) {
      const selection: ListingDrawerHotelDateSelection = {
        type: 'flexible',
        flexibleMonths: addFlexibleMonthsIfNoneSelected(nights, params.flexibleMonths) || undefined,
        flexibleNights: nights,
        userSelectedFlexibleMonths: !!params.flexibleMonths,
      }
      if (!selection.flexibleMonths) return selection
      return getValidHotelDateSelection(selection) ?? { type: 'anytime' }
    }
  }

  const selection = getDefaultHotelSearchDateSelection(params.checkIn, params.checkOut)
  const today = new Date()
  return getValidHotelDateSelection(selection, today, { minDate: today }) ?? { type: 'anytime' }
}

export function areHotelSearchDateSelectionsEqual(
  left: ListingDrawerHotelDateSelection,
  right: ListingDrawerHotelDateSelection,
): boolean {
  if (left.type !== right.type) return false

  if (left.type === 'specific' && right.type === 'specific') {
    return left.checkIn === right.checkIn && left.checkOut === right.checkOut
  }

  if (left.type === 'flexible' && right.type === 'flexible') {
    return left.flexibleMonths === right.flexibleMonths &&
      left.flexibleNights === right.flexibleNights &&
      left.userSelectedFlexibleMonths === right.userSelectedFlexibleMonths
  }

  return true
}

export function getHotelSearchDateSelectionKey(
  selection: ListingDrawerHotelDateSelection,
): string {
  if (selection.type === 'specific') {
    return `specific:${selection.checkIn}:${selection.checkOut}`
  }

  if (selection.type === 'flexible') {
    return `flexible:${selection.flexibleMonths ?? ''}:${selection.flexibleNights}:${selection.userSelectedFlexibleMonths}`
  }

  return 'anytime'
}
